// R-1 minimal component showcase. R-5 rebuilds fully.
import type { ReactNode } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { Eyebrow } from '../components/Eyebrow';
import { Card } from '../components/Card';
import { StatusChip } from '../components/StatusChip';
import { NumberCallout } from '../components/NumberCallout';
import { Wordmark } from '../components/Wordmark';
import { FontSwitcher } from '../components/FontSwitcher';

const SWATCHES: { name: string; value: string }[] = [
  { name: 'canvas', value: '#F5F7FA' },
  { name: 'panel', value: '#FFFFFF' },
  { name: 'hairline', value: '#E5E8EC' },
  { name: 'ink', value: '#0B0D12' },
  { name: 'ink-muted', value: '#4B5563' },
  { name: 'ink-subtle', value: '#8A93A0' },
  { name: 'blue-deep', value: '#1D4ED8' },
  { name: 'blue', value: '#3B82F6' },
  { name: 'blue-light', value: '#60A5FA' },
  { name: 'blue-whisper', value: '#DBEAFE' },
];

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section className="border-t border-hairline pt-10 mt-10">
      <Eyebrow>{label}</Eyebrow>
      <div className="mt-6">{children}</div>
    </section>
  );
}

export function Showcase() {
  return (
    <>
      <Navbar />
      <main className="container-site py-16">
        <div className="flex items-center justify-between gap-6 flex-wrap">
          <Eyebrow>DESIGN_SYSTEM · R-1</Eyebrow>
          <FontSwitcher />
        </div>
        <h1 className="mt-4 font-display font-medium tracking-[-0.02em] text-[clamp(40px,6vw,72px)] leading-[1.05]">
          Components.
        </h1>
        <p className="mt-4 text-ink-muted max-w-2xl">
          R-1 primitives. Use these in every new feature; do not fork the system.
        </p>

        <Section label="WORDMARK">
          <div className="flex items-center gap-8">
            <Wordmark size="sm" />
            <Wordmark size="md" />
          </div>
        </Section>

        <Section label="PALETTE">
          <div className="flex flex-wrap gap-4">
            {SWATCHES.map((s) => (
              <div key={s.name} className="flex flex-col gap-2">
                <div
                  className="w-24 h-24 rounded-md border border-hairline"
                  style={{ backgroundColor: s.value }}
                />
                <div className="flex flex-col">
                  <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink">
                    {s.name}
                  </span>
                  <span className="font-mono text-[11px] text-ink-subtle">{s.value}</span>
                </div>
              </div>
            ))}
            <div className="flex flex-col gap-2">
              <div className="w-24 h-24 rounded-md bg-gradient-primary" />
              <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink">
                gradient
              </span>
            </div>
          </div>
        </Section>

        <Section label="TYPOGRAPHY">
          <div className="flex flex-col gap-6">
            <div>
              <Eyebrow>HERO</Eyebrow>
              <p className="mt-2 font-display font-medium tracking-[-0.02em] text-[clamp(44px,7vw,80px)] leading-[1.05]">
                Property insurance, done right.
              </p>
            </div>
            <div>
              <Eyebrow>H1</Eyebrow>
              <p className="mt-2 font-display font-medium text-[clamp(40px,6vw,72px)] leading-[1.05]">
                One self-serve form.
              </p>
            </div>
            <div>
              <Eyebrow>H2</Eyebrow>
              <p className="mt-2 font-display font-medium text-[clamp(32px,5vw,56px)] leading-[1.15]">
                Three scenarios.
              </p>
            </div>
            <div>
              <Eyebrow>H3</Eyebrow>
              <p className="mt-2 font-display font-medium text-[clamp(24px,3vw,32px)] leading-[1.3]">
                Real brokers behind it.
              </p>
            </div>
            <div>
              <Eyebrow>BODY</Eyebrow>
              <p className="mt-2 text-[1rem] text-ink-muted leading-relaxed max-w-xl">
                Body text in Space Grotesk 400. Cool ink-muted color. Reads quietly, not yelling.
              </p>
            </div>
            <div>
              <Eyebrow>MONO · EYEBROW</Eyebrow>
              <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.125em] text-ink-subtle">
                &gt; SAMPLE_LABEL · UNDERSCORED
              </p>
            </div>
          </div>
        </Section>

        <Section label="BUTTONS">
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="primary">Primary</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="primary" size="sm">
              Primary sm
            </Button>
            <Button variant="secondary" size="sm">
              Secondary sm
            </Button>
            <Button variant="primary" disabled>
              Disabled
            </Button>
          </div>
        </Section>

        <Section label="STATUS_CHIPS">
          <div className="flex flex-wrap items-center gap-4">
            <StatusChip label="IDLE_STATE" tone="idle" />
            <StatusChip label="ACTIVE_PROCESSING" tone="active" />
            <StatusChip label="DONE_OK" tone="done" />
            <StatusChip label="ERROR_FAIL" tone="error" />
          </div>
        </Section>

        <Section label="NUMBER_CALLOUTS">
          <div className="flex flex-wrap gap-10">
            <NumberCallout eyebrow="TIV" value="$34.0M" size="lg" />
            <NumberCallout eyebrow="UNITS" value="189" size="md" />
            <NumberCallout eyebrow="PROJECT_DAYS" value="669" unit="d" size="md" />
            <NumberCallout eyebrow="CLASS" value="FRAME" size="sm" />
          </div>
        </Section>

        <Section label="CARDS">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card variant="panel">
              <Eyebrow>PANEL</Eyebrow>
              <p className="mt-2 font-display text-[1rem]">Default surface with soft shadow.</p>
            </Card>
            <Card variant="hairline">
              <Eyebrow>HAIRLINE</Eyebrow>
              <p className="mt-2 font-display text-[1rem]">Border only, no shadow.</p>
            </Card>
            <Card variant="hero">
              <Eyebrow>HERO</Eyebrow>
              <p className="mt-2 font-display text-[1rem]">Bigger padding for hero moments.</p>
            </Card>
          </div>
        </Section>
      </main>
      <Footer />
    </>
  );
}
